/**
 * Auth Module UI Types
 *
 * Frontend types optimized for UI components.
 * Transformed from backend DTOs for optimal frontend usage.
 */

import type { UserResponse } from '@bat/shared'

import { ApiException } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system'
  currency: string
  timezone: string
  language: string
  notifications: {
    email: boolean
    push: boolean
    desktop: boolean
    securityAlerts: boolean
    systemUpdates: boolean
    marketing: boolean
  }
  dateFormat: 'DD.MM.YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD' | 'DD/MM/YYYY'
  numberFormat:
    | 'europeo-puntos'
    | 'europeo-sin-puntos'
    | 'americano-comas'
    | 'americano-sin-comas'
    | 'european'
    | 'american'
    | 'international'
    | 'compact'
  compactMode?: boolean
  autoSaveEnabled?: boolean
}

export interface User {
  id: string
  email: string
  /** A built-in role (`super_admin`, `org_admin`, `user`, `viewer`) or a custom role of the organisation. */
  role: string
  status: 'active' | 'inactive' | 'suspended' | 'pending'
  emailVerified: boolean

  // Date objects for DatePickers and temporal logic
  lastLoginAt?: Date | null
  createdAt: Date
  updatedAt: Date

  // Account settings (the account holds no personal identity: no names, phone, bio, avatar or company)
  timezone?: string
  preferences?: UserPreferences
}

/**
 * Auth Modal State
 */
export interface AuthModalState {
  isOpen: boolean
  mode: 'login' | 'register'
  isTransitioning: boolean
  isLoading: boolean
  error: string | null
}

/**
 * Login Data for form submission
 */
export interface LoginData {
  email: string
  password: string
  rememberMe: boolean
}

/**
 * Register Data for form submission
 */
export interface RegisterData {
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean // Changed from termsAccepted to match backend contract
}

/**
 * Login Form State
 */
export interface LoginFormState {
  email: string
  password: string
  rememberMe: boolean
  isLoading: boolean
  isSubmitting: boolean
  error: string | null
  errors: Record<string, string>
  fieldErrors: Record<string, string>
}

/**
 * Register Form State
 */
export interface RegisterFormState {
  email: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean // Changed from termsAccepted to match backend contract
  isLoading: boolean
  isSubmitting: boolean
  error: string | null
  errors: Record<string, string>
  fieldErrors: Record<string, string>
}

/** What the UI shows for the account: its email (the account keeps no names). */
export function getDisplayName(user: Pick<User, 'email'>): string {
  return user.email
}

/** Initials for an avatar: the first two letters of the email's local part. */
export function getInitials(user: Pick<User, 'email'>): string {
  return emailInitials(user.email)
}

/** The local part of an email (`ana@x.com` -> `ana`); the whole value when it has no `@`. */
export function emailLocalPart(email: string): string {
  const at = email.indexOf('@')
  return at > 0 ? email.slice(0, at) : email
}

/** Up to two upper-case letters/digits from the email's local part; `U` when there is none. */
export function emailInitials(email: string | null | undefined): string {
  const letters = emailLocalPart(email ?? '').replace(/[^\p{L}\p{N}]/gu, '')
  return letters.slice(0, 2).toUpperCase() || 'U'
}

/**
 * What a caller throws when `transformUserResponse` returns `null`: the server answered a user with no role. It is the
 * synthetic `BAD_RESPONSE` the login already uses for a malformed answer (empty message), so the screen shows its
 * translated generic copy and not a technical English sentence.
 */
export function userWithoutRoleError(): ApiException {
  return new ApiException({ status: 400, message: '', code: 'BAD_RESPONSE' })
}

/**
 * Transform UserResponse from API to UI User type.
 *
 * A user with no role (empty or not text) is not a session: guards and menus have nothing to decide with, and the role
 * is not invented (it used to be read as `user`). It returns `null` and logs the error; every caller treats `null` as
 * «no session», exactly as it treats an answer with no user.
 */
export function transformUserResponse(userResponse: UserResponse): User | null {
  const role: unknown = userResponse.role
  if (typeof role !== 'string' || role.trim() === '') {
    logger.error('User without a role received: the session is not started')
    return null
  }

  return {
    id: userResponse.id,
    email: userResponse.email,
    role: userResponse.role,
    status: userResponse.status,
    emailVerified: userResponse.emailVerified,
    lastLoginAt: userResponse.lastLoginAt ? new Date(userResponse.lastLoginAt) : null,
    createdAt: new Date(userResponse.createdAt),
    updatedAt: new Date(userResponse.updatedAt),
    timezone: userResponse.timezone,
    preferences: userResponse.preferences as UserPreferences | undefined,
  }
}
